import { type ChangeEvent, type FormEvent, useEffect, useRef } from 'react';
import {
  type ClientRegistrationFields,
  selectedCompany,
} from 'types/registration';
import { useTranslation } from 'utils/i18n';
import * as yup from 'yup';
import useClientServices from '../../../hooks/useClientServices';
// Hooks
import useValidation from '../../../hooks/useValidation';
import type { FieldChangeEvent } from '../../../types';
// Components
import CheckBoxList from '../../fields/checkBoxList';
import InputError from '../../fields/inputError';
import ServiceDetailsContainer from '../serviceDetailsContainer';

const schema = yup.object().shape({
  existingClientServices: yup.array().of(yup.string()),
});

interface Props {
  fields: ClientRegistrationFields;
  submissionErrors?: string[];
  handleFieldChange: (event: FieldChangeEvent<unknown>) => void;
  handleSubmitCallback: () => void;
  handleGoBack?: () => void;
  previousStep?: number;
}

const ExistingServiceDetails = ({
  fields,
  submissionErrors,
  handleFieldChange,
  handleSubmitCallback,
  handleGoBack,
  previousStep = 2,
}: Props) => {
  const { t } = useTranslation('common');
  const { errors, validate, isValid } = useValidation(fields, schema);
  const { data: clientServices = [] } = useClientServices(
    selectedCompany(fields.companyName)?.value
  );

  // Validate only when services change.
  const hasMounted = useRef(false);
  useEffect(() => {
    if (hasMounted.current) {
      validate();
    } else {
      hasMounted.current = true;
    }
  }, [validate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFieldChange(event);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    validate();
    if (isValid()) {
      handleSubmitCallback();
    }

    return false;
  };

  return (
    <ServiceDetailsContainer
      handleSubmit={handleSubmit}
      submissionErrors={submissionErrors}
      isValid={isValid()}
      handleGoBack={handleGoBack}
      previousStep={previousStep}
    >
      <div className="service-container service-container--existing">
        <h4>{t('registration.service_details.container_title_existing')}</h4>
        <CheckBoxList
          name="existingClientServices[]"
          options={clientServices.map(option => ({
            name: option.name,
            value: option.id,
            checked: fields.existingClientServices.includes(option.id),
          }))}
          id="existingClientServices"
          onChange={handleChange}
        />
        {errors.existingClientServices && (
          <InputError errorMsg={errors.existingClientServices} />
        )}

        <p>{t('registration.service_details.existing_company_instructions')}</p>
      </div>
    </ServiceDetailsContainer>
  );
};

export default ExistingServiceDetails;
